import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  FormControl,
  Select,
  OutlinedInput,
  MenuItem,
  Checkbox,
  ListItemText,
  Box,
  Divider,
  Chip,
} from '@mui/material';
import { Node } from 'reactflow';
import { WorkflowNodeData } from './WorkflowNode';
import LinkIcon from '@mui/icons-material/Link';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

interface ConnectionDialogProps {
  open: boolean;
  onClose: () => void;
  nodes: Node<WorkflowNodeData>[];
  targetNodeId: string | null;
  selectedSourceIds: string[];
  onSourceIdsChange: (ids: string[]) => void;
  onSave: () => void;
}

export const ConnectionDialog: React.FC<ConnectionDialogProps> = ({
  open,
  onClose,
  nodes,
  targetNodeId,
  selectedSourceIds,
  onSourceIdsChange,
  onSave,
}) => {
  const targetNode = nodes.find((n) => n.id === targetNodeId);

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      fullWidth 
      maxWidth="xs"
      PaperProps={{
        sx: { borderRadius: '12px', border: '1px solid #E2E8F0' }
      }}
    >
      <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <LinkIcon sx={{ color: "#B87333", fontSize: 20 }} />
        <Typography sx={{ fontWeight: 800, color: '#1E293B', fontSize: '1rem' }}>
          Configure Inputs
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ mt: 1 }}>
        {/* TARGET INDICATOR: Shows which node is receiving the input */}
        <Box sx={{ 
          bgcolor: '#F8FAFC', 
          px: 1.5, 
          py: 1,
          borderRadius: '8px', 
          mb: 2, 
          border: '1px solid #F1F5F9',
          textAlign: 'center'
        }}>
          <Typography sx={{ fontSize: '10px', fontWeight: 800, color: '#94A3B8' }}>
            Target Step
          </Typography>
          <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#334155' }}>
            {targetNode?.data.label || 'Unknown Step'}
          </Typography>
          <ArrowDownwardIcon sx={{ fontSize: 14, color: '#CBD5E1', mt: 0.5 }} />
        </Box>

        <Typography variant="caption" sx={{ fontWeight: 700, color: "#64748B", display: "block", mb: 1, ml: 0.5 }}>
          Select Input Sources
        </Typography>

        {/* FULL WIDTH SELECT */}
        <FormControl fullWidth size="small">
          <Select
            id="multiple-source-checkbox"
            multiple
            value={selectedSourceIds}
            onChange={(event) => {
              const { target: { value } } = event;
              onSourceIdsChange(typeof value === 'string' ? value.split(',') : value);
            }}
            input={<OutlinedInput />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {(selected as string[]).map((id) => {
                  const n = nodes.find((node) => node.id === id);
                  return (
                    <Chip 
                      key={id} 
                      label={n ? n.data.label : id} 
                      size="small"
                      sx={{ 
                        bgcolor: '#F1F5F9', 
                        fontWeight: 700, 
                        fontSize: '10px', 
                        borderRadius: '4px',
                        height: '22px'
                      }}
                    />
                  );
                })}
              </Box>
            )}
            sx={{
              bgcolor: '#FFFFFF',
              borderRadius: '8px',
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E2E8F0' },
            }}
          >
            {nodes
              .filter((n) => n.id !== targetNodeId)
              .map((node) => (
                <MenuItem key={node.id} value={node.id} sx={{ py: 0.5 }}>
                  <Checkbox 
                    checked={selectedSourceIds.indexOf(node.id) > -1} 
                    size="small"
                    sx={{ color: '#CBD5E1', '&.Mui-checked': { color: '#B87333' } }}
                  />
                  <ListItemText 
                    primary={node.data.label} 
                    primaryTypographyProps={{ fontSize: '13px', fontWeight: 600 }}
                    secondary={node.id}
                    secondaryTypographyProps={{ fontSize: '10px' }}
                  />
                </MenuItem>
              ))}
          </Select>
        </FormControl>

        <Typography sx={{ fontSize: '11px', color: '#94A3B8', mt: 2, fontStyle: 'italic' }}>
          Data from the selected sources will be passed to this node as context.
        </Typography>
      </DialogContent>

      <Divider sx={{ opacity: 0.6 }} />

      <DialogActions sx={{ p: 2, bgcolor: '#FDFDFD' }}>
        <Button 
          onClick={onClose} 
          sx={{ textTransform: 'none', fontWeight: 700, color: '#64748B', fontSize: '12px' }}
        >
          Cancel
        </Button>
        <Button 
          variant="contained" 
          onClick={onSave}
          color="primary"
          disableElevation
          size="small"
          >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};